// The innernet wordmark — the one canonical lockup. Inline SVG of the Figma
// letterform (lowercase humanist sans with the leaf flick on the first stroke +
// the i-dot), so it recolours by `currentColor` and scales crisply at any size.
//
// SIZING LAW: height is set in `em` and the mark is baseline-aligned, so dropped
// next to live text it reads as the exact same size and sits on the same baseline
// — the wordmark "feels part of one thing" with the words beside it. The default
// 0.72em maps the letterform's x-height onto a typical sans x-height; nudge per
// context with `height`.
//
// INLINE-WITH-TEXT LAW: any call site that sets the wordmark next to live prose
// (breadcrumbs, headlines, etc.) should size it off WORDMARK_INLINE_EM rather
// than inventing its own height — that's the one ratio tuned so the mark's
// optical x-height lands on the surrounding text's, regardless of context.
import { useId } from "react";
import { WORDMARK, BRAND } from "../../lib/brand/marks";

export type WordmarkTone = "current" | "solid" | "gradient" | "accent";

// The wordmark's letterform x-height as a fraction of its rendered height; sizing
// the mark to this fraction of an em lands its letters on adjacent text's
// letter-height. Single source of truth for every inline (baseline-aligned) use —
// breadcrumbs, the landing title, and anywhere else the mark sits beside text.
export const WORDMARK_INLINE_EM = "0.68em";

export default function Wordmark({
  height = "0.72em",
  tone = "current",
  title,
  className,
  style,
}: {
  /** any CSS length; em-based by default so it tracks the surrounding font-size */
  height?: string | number;
  tone?: WordmarkTone;
  /** accessible name; omit to render decorative (aria-hidden) */
  title?: string;
  className?: string;
  style?: React.CSSProperties;
}) {
  const id = useId().replace(/[:]/g, "");
  const gradId = `wm-grad-${id}`;
  const fill =
    tone === "gradient" ? `url(#${gradId})`
      : tone === "solid" ? BRAND.blue
      : tone === "accent" ? "var(--accent-ink)"
      : "currentColor";
  return (
    <svg
      viewBox={WORDMARK.viewBox}
      role={title ? "img" : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      focusable="false"
      className={className}
      style={{
        height,
        width: "auto",
        display: "inline-block",
        verticalAlign: "baseline",
        // the leaf flick + i-dot sit just outside the letterform's tight box; let
        // them paint past the svg viewport so the mark never clips — especially
        // small, on mobile crumbs where the surrounding line box is tight.
        overflow: "visible",
        fill,
        flexShrink: 0,
        ...style,
      }}
    >
      {title ? <title>{title}</title> : null}
      {tone === "gradient" && (
        <defs>
          <linearGradient id={gradId} x1="368" y1="0" x2="368" y2={154.812} gradientUnits="userSpaceOnUse">
            <stop stopColor={BRAND.wordmarkGrad[0]} />
            <stop offset="1" stopColor={BRAND.wordmarkGrad[1]} />
          </linearGradient>
        </defs>
      )}
      <path d={WORDMARK.union} />
      <circle cx={WORDMARK.dot.cx} cy={WORDMARK.dot.cy} r={WORDMARK.dot.r} />
      <path d={WORDMARK.leaf} />
    </svg>
  );
}
